import { RoleType } from '@hit-game/shared/enums';
import { useEffect, useState, type FormEvent } from 'react';

import { Button } from '../common/Button';
import { Dropdown } from '../common/Dropdown';
import { FIELD_CLASS_BOLD as FIELD_CLASS, LABEL_CLASS } from '../common/form.styles';
import { Modal } from '../common/Modal';

const MODAL_WIDTH = 480;

/** Khớp `createUserSchema` bên server — báo ngay tại form thay vì đợi một vòng request. */
const MIN_USERNAME_LENGTH = 3;
const MIN_PASSWORD_LENGTH = 6;

const ROLE_OPTIONS = [
  { value: RoleType.USER, label: 'USER — chỉ quản lý tài nguyên của mình' },
  { value: RoleType.ADMIN, label: 'ADMIN — toàn quyền' },
];

export interface NewUserInput {
  username: string;
  password: string;
  note?: string;
  role: RoleType;
}

interface UserCreateModalProps {
  open: boolean;
  onClose: () => void;
  /** Trả `true` khi tạo được — hộp thoại tự đóng. */
  onSubmit: (input: NewUserInput) => Promise<boolean>;
}

/** Hộp thoại tạo tài khoản vận hành. Mật khẩu để dạng chữ thường để admin đọc lại được. */
export function UserCreateModal({ open, onClose, onSubmit }: UserCreateModalProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [note, setNote] = useState('');
  const [role, setRole] = useState<string>(RoleType.USER);
  const [submitting, setSubmitting] = useState(false);

  // Dọn form mỗi lần mở: giữ state cũ thì lần tạo sau hiện credential lần trước.
  useEffect(() => {
    if (!open) return;
    setUsername('');
    setPassword('');
    setNote('');
    setRole(RoleType.USER);
  }, [open]);

  const canSubmit =
    username.trim().length >= MIN_USERNAME_LENGTH && password.length >= MIN_PASSWORD_LENGTH;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    const ok = await onSubmit({
      username: username.trim(),
      password,
      note: note.trim() || undefined,
      role: role as RoleType,
    });
    setSubmitting(false);
    if (ok) onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={submitting}
      title="Thêm người dùng"
      maxWidth={MODAL_WIDTH}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Hủy
          </Button>
          <Button
            onClick={(event) => {
              void handleSubmit(event);
            }}
            loading={submitting}
            disabled={submitting || !canSubmit}
            className="px-5"
          >
            {submitting ? 'Đang tạo...' : 'Tạo tài khoản'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="new-username" className={LABEL_CLASS}>
            Tên đăng nhập <span className="text-danger">*</span>
          </label>
          <input
            id="new-username"
            type="text"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
            }}
            placeholder={`Tối thiểu ${String(MIN_USERNAME_LENGTH)} ký tự`}
            className={FIELD_CLASS}
          />
        </div>

        <div>
          <label htmlFor="new-password" className={LABEL_CLASS}>
            Mật khẩu <span className="text-danger">*</span>
          </label>
          <input
            id="new-password"
            type="text"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            placeholder={`Tối thiểu ${String(MIN_PASSWORD_LENGTH)} ký tự`}
            className={FIELD_CLASS}
          />
        </div>

        <div>
          <label className={LABEL_CLASS}>Vai trò</label>
          <Dropdown value={role} onChange={setRole} options={ROLE_OPTIONS} searchable={false} />
        </div>

        <div>
          <label htmlFor="new-note" className={LABEL_CLASS}>
            Ghi chú
          </label>
          <input
            id="new-note"
            type="text"
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
            }}
            placeholder="Tuỳ chọn"
            className={FIELD_CLASS}
          />
        </div>
      </div>
    </Modal>
  );
}
